import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, inCubic, inOut, inQuad, outCubic, outExpo, phase, settle, span } from '../curves.ts';

/*
 * The Chasm's props (models/sima-props.ts): they stand still. 'idle' (and 'walk', 'attack', 'stun', the same
 * loop: a prop has nothing else to do) breathes a little, 'hit' shudders, 'die' breaks them apart.
 */

type C = 'root' | 'shardA' | 'shardB' | 'shardC';
type K = 'root' | 'shellL' | 'shellR' | 'brood';
type I = 'root' | 'iceFL' | 'iceFR' | 'iceBL' | 'iceBR';

const shudder = (t: number): number => settle(t, 5, 9) * span(t, 0, 0.03);
const squashHit = (t: number): [number, number, number] => {
  const q = Math.max(0, settle(t, 4, 9));
  return [1 + 0.05 * q, 1 - 0.06 * q, 1 + 0.05 * q];
};

/* ---------- the crystal ---------- */

const crystalIdle = (name: string): Clip<C> =>
  clip<C>(name, 3, true, (b, t) => {
    const p = phase(t, 3) * TAU;
    b.shardA.scale(1 + 0.01 * Math.sin(p));
    b.shardB.rot(0, 0, 0.01 * Math.sin(p + 1));
    b.shardC.rot(0, 0, -0.01 * Math.sin(p + 2));
  });
const crystalHit = (): Clip<C> =>
  clip<C>('hit', 0.45, false, (b, t) => {
    const q = shudder(t);
    b.root.rot(0.04 * q, 0, 0.05 * q);
    b.shardB.rot(0, 0, -0.08 * q);
    b.shardC.rot(0, 0, 0.08 * q);
  }, squashHit);
/** It shatters: the three clumps fall apart outward and sink away. */
const crystalDie = (): Clip<C> =>
  clip<C>('die', 1.4, false, (b, t) => {
    const k = span(t, 0.05, 0.7, outExpo);
    const g = span(t, 0.6, 1.4, inQuad);
    b.shardA.rot(0.5 * k, 0, 0.2 * k).pos(0, -0.4 * g, 0.1 * k).scale(1 - 0.6 * g);
    b.shardB.rot(0, 0, -1.1 * k).pos(0.25 * k, -0.4 * g, 0).scale(1 - 0.6 * g);
    b.shardC.rot(0, 0, 1.1 * k).pos(-0.25 * k, -0.4 * g, 0).scale(1 - 0.6 * g);
  }, (t) => {
    const q = Math.max(0, settle(t, 4, 8));
    return [1 + 0.08 * q, 1 - 0.1 * q, 1 + 0.08 * q];
  });

/* ---------- the cocoon ---------- */

const pulse = (b: Bones<K>, t: number, dur: number): void => {
  const p = phase(t, dur) * TAU;
  const beat = Math.max(0, Math.sin(p)) ** 3;
  b.shellL.rot(0, 0, 0.03 * beat).scale(1 + 0.04 * beat);
  b.shellR.rot(0, 0, -0.03 * beat).scale(1 + 0.04 * beat);
  b.brood.scale(1 + 0.25 * beat).rot(0, p, 0);
  b.root.rot(0.02 * Math.sin(p * 3) * beat, 0, 0.02 * Math.sin(p * 2) * beat);
};
const cocoonIdle = (name: string): Clip<K> => clip<K>(name, 1.6, true, (b, t) => pulse(b, t, 1.6));
const cocoonHit = (): Clip<K> =>
  clip<K>('hit', 0.45, false, (b, t) => {
    const q = shudder(t);
    b.root.rot(0.08 * q, 0, 0.1 * q);
    b.brood.scale(1 + 0.3 * Math.max(0, q));
  }, squashHit);
/** It splits: the halves tear apart and fall open, the brood's light flares and goes out. */
const cocoonDie = (): Clip<K> =>
  clip<K>('die', 1.3, false, (b, t) => {
    const k = span(t, 0.05, 0.45, outCubic);
    const g = span(t, 0.7, 1.3, inCubic);
    b.shellL.rot(0, 0, 1.2 * k).pos(-0.05 * k, -0.15 * g, 0);
    b.shellR.rot(0, 0, -1.2 * k).pos(0.05 * k, -0.15 * g, 0);
    b.brood.scale(Math.max(0.01, (1 + 0.8 * span(t, 0, 0.3)) * (1 - span(t, 0.3, 0.8, inOut))));
  }, (t) => {
    const q = Math.max(0, settle(t, 4, 8));
    return [1 + 0.06 * q, 1 - 0.08 * q, 1 + 0.06 * q];
  });

/* ---------- the ice ---------- */

const iceIdle = (name: string): Clip<I> =>
  clip<I>(name, 3, true, (b, t) => {
    // Still as ice; only the faintest creak.
    const p = phase(t, 3) * TAU;
    b.root.rot(0, 0, 0.004 * Math.sin(p));
  });
const iceHit = (): Clip<I> =>
  clip<I>('hit', 0.4, false, (b, t) => {
    const q = shudder(t);
    b.root.rot(0.03 * q, 0, 0.04 * q);
    b.iceFL.pos(-0.02 * q, 0, 0.02 * q);
    b.iceFR.pos(0.02 * q, 0, 0.02 * q);
  }, squashHit);
/** It bursts: the four quarters blow outward, tip over and sink, the hero stepping out of them. */
const iceDie = (): Clip<I> =>
  clip<I>('die', 1.2, false, (b, t) => {
    const k = span(t, 0, 0.35, outExpo);
    const g = span(t, 0.45, 1.2, inQuad);
    const out = (kx: number, kz: number): [number, number, number] => [0.45 * kx * k, -0.9 * g, 0.4 * kz * k];
    b.iceFL.pos(...out(-1, 1)).rot(0.6 * k, 0, 0.6 * k).scale(1 - 0.7 * g);
    b.iceFR.pos(...out(1, 1)).rot(0.6 * k, 0, -0.6 * k).scale(1 - 0.7 * g);
    b.iceBL.pos(...out(-1, -1)).rot(-0.6 * k, 0, 0.6 * k).scale(1 - 0.7 * g);
    b.iceBR.pos(...out(1, -1)).rot(-0.6 * k, 0, -0.6 * k).scale(1 - 0.7 * g);
  });

/** Clips by library kind (see ../rigs.ts). */
export const crystalClips = (): Clip[] => [crystalIdle('idle'), crystalIdle('walk'), crystalIdle('attack'), crystalHit(), crystalIdle('stun'), crystalDie()] as Clip[];
export const cocoonClips = (): Clip[] => [cocoonIdle('idle'), cocoonIdle('walk'), cocoonIdle('attack'), cocoonHit(), cocoonIdle('stun'), cocoonDie()] as Clip[];
export const iceBlockClips = (): Clip[] => [iceIdle('idle'), iceIdle('walk'), iceIdle('attack'), iceHit(), iceIdle('stun'), iceDie()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { crystal: crystalClips, cocoon: cocoonClips, iceBlock: iceBlockClips };
